import { defineStore } from 'pinia';
// import { LocalStorage } from 'quasar';
import type { dynamicObj } from 'src/pages/assets';
import { documentPermissionsApiService } from 'src/services';
import { filterStoreDocTypes } from 'src/utils/docTypeFilter';
import { Acl, AccessStatus, PermissionLevel } from 'src/core/acls/index';

export const useDocPermissionsStore = defineStore('doc_permissions', {
  state: () => ({
    groups: [] as any,
    group_id: '',
    processing: false,
    selected_permissions: [] as dynamicObj,
    apiData: [] as dynamicObj,
    accessOptionList: [
      { label: 'Enabled', value: AccessStatus.ENABLED },
      { label: 'Disabled', value: AccessStatus.DISABLED },
    ],
    listOption: [
      { label: 'No Access', value: PermissionLevel.NO_ACCESS },
      { label: 'Everyone', value: PermissionLevel.FULL },
      { label: 'Owner', value: PermissionLevel.OWNER },
      { label: 'Assignee', value: PermissionLevel.ASSIGNEE },
      { label: 'Owner & Assignee', value: PermissionLevel.OWNER_ASSIGNEE },
    ],
    fields: [
      'access',
      'list',
      'view',
      'edit',
      'delete',
      'mass_update',
      'first_approval',
      'second_approval',
      // 'unlock_document',
    ],
  }),
  getters: {
    getPermissionsByGroupId: (state: any) => (id: string) => {
      const group = state.groups.find((g: any) => g.group_id === id) as
        | dynamicObj
        | undefined;
      if (group?.doc_type_permissions) {
        group.doc_type_permissions = filterStoreDocTypes(group.doc_type_permissions);
      }
      return group;
    },
  },
  actions: {
    async getPermissionsData(params = {}) {
      try {
        this.processing = true;
        const response = await documentPermissionsApiService.getAll(params);
        if (!response) {
          throw response;
        }
        this.groups = response.map((item: any) => ({
          group_id: item.group_id,
          group_name: item.group_name,
          description: item.description,
          only_assigned_documents: item.only_assigned_documents,
          doc_type_permissions: item.doc_type_permissions,
        }));
        await Acl.fetch();
      } catch (err: any) {
        throw err;
      } finally {
        this.processing = false;
      }
    },

    async deleteGroupById(id: string) {
      this.processing = true;
      const index = this.groups.findIndex(
        (group: any) => group.group_id === id,
      );
      if (index === -1) {
        return;
      }
      await documentPermissionsApiService.deleteGroupById(id);
      this.groups.splice(index, 1);
    },

    async editGroup(id: string, data: any): Promise<any> {
      await documentPermissionsApiService.editGroup(id, data);
      // Update in local list instead of re-fetching
      const index = this.groups.findIndex((g: any) => g.group_id === id);
      if (index !== -1) {
        this.groups[index] = {
          ...this.groups[index],
          group_name: data.name,
          description: data.description,
          only_assigned_documents: data.only_assigned_documents,
        };
      }
    },

    async createGroup(group: dynamicObj): Promise<any> {
      const data = {
        name: group.name,
        description: group.description,
      };
      const response = await documentPermissionsApiService.createGroup(data);
      // Push to local list instead of re-fetching all
      this.groups.push({
        group_id: response.id,
        group_name: response.name,
        description: response.description,
        only_assigned_documents: response.only_assigned_documents,
        doc_type_permissions: response.doc_type_permissions || [],
      });
      return response;
    },

    mappedPermissionToDocument(documentTypes: dynamicObj[]) {
      const permissions = this.apiData[this.group_id];
      if (!permissions) return;
      documentTypes.forEach((doc: dynamicObj) => {
        const defaults = {
          access: AccessStatus.DISABLED,
          // When the API omits an action, default the select to "No Access"
          // so admins can't accidentally save FULL access for unset rows.
          list: PermissionLevel.NO_ACCESS,
          view: PermissionLevel.NO_ACCESS,
          edit: PermissionLevel.NO_ACCESS,
          delete: PermissionLevel.NO_ACCESS,
          mass_update: PermissionLevel.NO_ACCESS,
          first_approval: PermissionLevel.NO_ACCESS,
          second_approval: PermissionLevel.NO_ACCESS,
          // unlock_document: PermissionLevel.NO_ACCESS,
        } as dynamicObj;

        if (!permissions[doc.document_key]) {
          //not found in permissions object
          permissions[doc.document_key] = { ...defaults };
        } else {
          Object.keys(defaults).forEach((key: any) => {
            if (!permissions[doc.document_key].hasOwnProperty(key)) {
              permissions[doc.document_key][key] = defaults[key] as number;
            }
          });
        }
      });
    },

    async getAllDocTypeSettings(group_id: any) {
      this.group_id = group_id;
      const response =
        await documentPermissionsApiService.getAllDocTypeSettings(group_id);
      if (response) {
        this.apiData = response;
      }
    },

    async getAllDocTypeFieldsForAccessControl(group_id: any): Promise<any> {
      try {
        const rs =
          await documentPermissionsApiService.getAllFieldsDocTypeSettings(
            group_id,
          );
        if (!rs) throw rs;

        // Merge field data with existing apiData instead of overwriting
        if (rs[group_id]) {
          // Ensure the group exists in apiData
          if (!this.apiData[group_id]) {
            this.apiData[group_id] = {};
          }

          // Merge each document's field data
          Object.keys(rs[group_id]).forEach((docKey) => {
            if (!this.apiData[group_id][docKey]) {
              this.apiData[group_id][docKey] = rs[group_id][docKey];
            } else {
              // Merge fields while preserving existing permission data
              // (server response is spread first so unsaved local edits win)
              this.apiData[group_id][docKey] = {
                ...rs[group_id][docKey],
                ...this.apiData[group_id][docKey],
              };
            }
          });
        }
      } catch (error) {
        throw error;
      }
    },

    async updateDocTypeSetting(data: any) {
      const formData = new FormData();
      formData.append('data', data);
      await documentPermissionsApiService.updateAllDocTypeSettings(formData);
    },

    async getUnlockRole(doc_type: string, assignee_id: string) {
      // unlock_document permission disabled — return false so only admins can unlock.
      // const group_ids = await this.getGroupIds();
      // let unlock_role = false;
      // const user_info: any = LocalStorage.getItem('user_details');
      // const user_id = user_info.id;
      //
      // for (const group_id of group_ids) {
      //   const response =
      //     await documentPermissionsApiService.getAllFieldsDocTypeSettings(
      //       group_id,
      //     );
      //   const temp_resp = response[group_id];
      //
      //   if (
      //     temp_resp &&
      //     temp_resp[doc_type] &&
      //     'unlock_document' in temp_resp[doc_type]
      //   ) {
      //     const unlockDocumentValue = temp_resp[doc_type].unlock_document;
      //
      //     if (unlockDocumentValue === PermissionLevel.FULL) {
      //       // Everyone can unlock
      //       unlock_role = true;
      //       break;
      //     } else if (
      //       (unlockDocumentValue & (PermissionLevel.OWNER | PermissionLevel.ASSIGNEE)) &&
      //       user_id === assignee_id
      //     ) {
      //       // Only owner/assignee can unlock
      //       unlock_role = true;
      //       break;
      //     }
      //   }
      // }
      //
      // return unlock_role;
      void doc_type;
      void assignee_id;
      return false;
    },

    async getGroupIds() {
      const response = await documentPermissionsApiService.getUserGroups();
      const allGroupIds: [] = response.map((item: any) => item.id as string);
      return allGroupIds;
    },
  },
});
